import { useState, useEffect } from 'react';
import { collection, doc, getDoc, getDocs, setDoc, updateDoc, serverTimestamp, addDoc, increment } from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';
import CSVImportHandler from '../../imports/CSVImportHandler';

const ExperienceSection = ({ db, onComplete }) => {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState({
    flightHoursLast30Days: 0,
    last90DaysFlightHours: 0,
    nightHoursLast30Days: 0,
    totalXCHours: 0,
    totalPICHours: 0,
    totalFlightHours: 0,
  });
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [hoursInType, setHoursInType] = useState([]);
  const [selectedTypeDesignator, setSelectedTypeDesignator] = useState('');
  const [typeHours, setTypeHours] = useState('');
  const [availableDesignators, setAvailableDesignators] = useState([]);
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [editingExperienceId, setEditingExperienceId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [quickEntryData, setQuickEntryData] = useState({
    flightHoursLast30Days: '',
    last90DaysFlightHours: '',
    nightHoursLast30Days: '',
    pic: '',
    sic: '',
    simulatedflight: '',
    simulatedinstrument: '',
    solo: '',
    totaltime: '',
    night: '',
    dualgiven: '',
    dualreceived: '',
    groundtraining: '',
    alllandings: '',
    actualinstrument: '',
    daytakeoffs: '',
    nighttakeoffs: '',
    nightlandingsfullstop: '',
    crosscountry: '',
    distance: ''
  });

  useEffect(() => {
    const loadProfileData = async () => {
      try {
        const pilotDocRef = doc(db, `pilot/${user.uid}`);
        const pilotDoc = await getDoc(pilotDocRef);

        if (pilotDoc.exists()) {
          setProfileData(pilotDoc.data());
        }
      } catch (err) {
        console.error('Error loading profile data:', err);
        setError('Failed to load profile data.');
      } finally {
        setLoading(false);
      }
    };

    const loadExperiences = async () => {
      try {
        const experienceCollectionRef = collection(db, `pilot/${user.uid}/experience`);
        const querySnapshot = await getDocs(experienceCollectionRef);

        const experienceList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setExperiences(experienceList);
      } catch (err) {
        console.error('Error loading flight experiences:', err);
        setError('Failed to load flight experiences.');
      }
    };

    const loadHoursInType = async () => {
      try {
        const pilotDocRef = doc(db, `pilot/${user.uid}`);
        const pilotDoc = await getDoc(pilotDocRef);
        
        if (pilotDoc.exists()) {
          setHoursInType(pilotDoc.data().hours_in_type || []);
        }

        // Get unique designators from user's aircraft
        const aircraftRef = collection(db, `pilot/${user.uid}/aircraft`);
        const aircraftSnap = await getDocs(aircraftRef);
        const designators = [...new Set(aircraftSnap.docs
          .map(doc => doc.data().typecode)
          .filter(Boolean))];
        setAvailableDesignators(designators.sort());
      } catch (err) {
        console.error('Error loading hours in type:', err);
        setError('Failed to load hours in type data.');
      }
    };

    const loadPilotData = async () => {
      try {
        const pilotRef = doc(db, `pilot/${user.uid}`);
        const pilotDoc = await getDoc(pilotRef);
        const pilotData = pilotDoc.data() || {};

        // Update quickEntryData with existing totals
        setQuickEntryData({
          flightHoursLast30Days: pilotData.flightHoursLast30Days?.toFixed(1) || '',
          last90DaysFlightHours: pilotData.last90DaysFlightHours?.toFixed(1) || '',
          nightHoursLast30Days: pilotData.nightHoursLast30Days?.toFixed(1) || '',
          pic: pilotData.totalPICHours?.toFixed(1) || '',
          sic: pilotData.totalSICHours?.toFixed(1) || '',
          simulatedflight: pilotData.totalSimulatorHours?.toFixed(1) || '',
          simulatedinstrument: pilotData.totalSimulatorIFCHours?.toFixed(1) || '',
          solo: pilotData.totalSoloHours?.toFixed(1) || '',
          totaltime: pilotData.totalFlightHours?.toFixed(1) || '',
          night: pilotData.totalNightHours?.toFixed(1) || '',
          dualgiven: pilotData.totalDualInstructionHoursGiven?.toFixed(1) || '',
          dualreceived: pilotData.totalDualInstructionHoursReceived?.toFixed(1) || '',
          groundtraining: pilotData.totalGroundTrainingHours?.toFixed(1) || '',
          alllandings: pilotData.totalLandings || '',
          actualinstrument: pilotData.totalInstrument?.toFixed(1) || '',
          daytakeoffs: pilotData.totalTakeoffs || '',
          nighttakeoffs: pilotData.totalNightTakeoffs || '',
          nightlandingsfullstop: pilotData.totalLandingsFullStop || '',
          crosscountry: pilotData.totalXCHours?.toFixed(1) || '',
          distance: pilotData.totalDistance || '',
          ipc: false // IPC is not stored as a total
        });
      } catch (err) {
        console.error('Error loading pilot data:', err);
        setError('Failed to load pilot data');
      }
    };

    if (db && user?.uid) {
      loadProfileData();
      loadExperiences();
      loadHoursInType();
      loadPilotData();
    }
  }, [db, user]);

  const handleUpdate = async (field, value) => {
    try {
      const pilotDocRef = doc(db, `pilot/${user.uid}`);
      const updatedValue = parseInt(value, 10);

      const updates = {
        [field]: updatedValue,
        totalFlightHours: profileData.totalFlightHours - profileData[field] + updatedValue,
      };

      await updateDoc(pilotDocRef, updates);

      setProfileData(prev => ({
        ...prev,
        ...updates,
      }));

      onComplete?.();
    } catch (err) {
      console.error('Error updating profile data:', err);
    }
  };

  const handleAddExperience = async () => {
    // Logic to add a new experience
    // This is a placeholder for the actual implementation
    console.log("Add Experience functionality triggered");
  };

  const handleUpdateHoursInType = async () => {
    if (!selectedTypeDesignator || !typeHours) {
      setError('Please select an aircraft type and enter hours.');
      return;
    }

    try {
      const pilotDocRef = doc(db, `pilot/${user.uid}`);
      const pilotDoc = await getDoc(pilotDocRef);
      const currentHoursInType = pilotDoc.data()?.hours_in_type || [];

      // Remove existing entry if present
      const filteredHours = currentHoursInType.filter(
        entry => entry.designator !== selectedTypeDesignator
      );

      // Add new entry
      const updatedHoursInType = [
        ...filteredHours,
        {
          designator: selectedTypeDesignator,
          total: parseFloat(typeHours)
        }
      ];

      await updateDoc(pilotDocRef, {
        hours_in_type: updatedHoursInType
      });

      setHoursInType(updatedHoursInType);
      setSelectedTypeDesignator('');
      setTypeHours('');
      onComplete?.();
    } catch (err) {
      console.error('Error updating hours in type:', err);
      setError('Failed to update hours in type.');
    }
  };

  const handleScanExperience = async () => {
    try {
      setLoading(true);
      
      // Get pilot document to check experienceLastUpdated and current totals
      const pilotRef = doc(db, `pilot/${user.uid}`);
      const pilotDoc = await getDoc(pilotRef);
      const pilotData = pilotDoc.data() || {};
      const lastUpdate = pilotData.experienceLastUpdated?.toDate() || null;

      // Initialize totals with existing values from pilot document
      const totals = {
        totalPICHours: parseFloat((parseFloat(pilotData.totalPICHours || 0)).toFixed(2)),
        totalSICHours: parseFloat((parseFloat(pilotData.totalSICHours || 0)).toFixed(2)),
        totalSimulatorHours: parseFloat((parseFloat(pilotData.totalSimulatorHours || 0)).toFixed(2)),
        totalSimulatorIFCHours: parseFloat((parseFloat(pilotData.totalSimulatorIFCHours || 0)).toFixed(2)),
        totalSoloHours: parseFloat((parseFloat(pilotData.totalSoloHours || 0)).toFixed(2)),
        totalFlightHours: parseFloat((parseFloat(pilotData.totalFlightHours || 0)).toFixed(2)),
        totalNightHours: parseFloat((parseFloat(pilotData.totalNightHours || 0)).toFixed(2)),
        totalDualInstructionHoursGiven: parseFloat((parseFloat(pilotData.totalDualInstructionHoursGiven || 0)).toFixed(2)),
        totalDualInstructionHoursReceived: parseFloat((parseFloat(pilotData.totalDualInstructionHoursReceived || 0)).toFixed(2)),
        totalGroundTrainingHours: parseFloat((parseFloat(pilotData.totalGroundTrainingHours || 0)).toFixed(2)),
        totalLandings: parseInt(pilotData.totalLandings || 0),
        totalInstrument: parseFloat((parseFloat(pilotData.totalInstrument || 0)).toFixed(2)),
        totalTakeoffs: parseInt(pilotData.totalTakeoffs || 0),
        totalNightTakeoffs: parseInt(pilotData.totalNightTakeoffs || 0),
        totalLandingsFullStop: parseInt(pilotData.totalLandingsFullStop || 0),
        totalLandingsNightFullStop: parseInt(pilotData.totalLandingsNightFullStop || 0),
        totalXCHours: parseFloat((parseFloat(pilotData.totalXCHours || 0)).toFixed(2)),
        totalDistance: parseFloat((parseFloat(pilotData.totalDistance || 0)).toFixed(2)),
        totalIFCHours: parseFloat((parseFloat(pilotData.totalIFCHours || 0)).toFixed(2))
      };

      // Get all experience entries
      const experienceRef = collection(db, `pilot/${user.uid}/experience`);
      const experienceSnap = await getDocs(experienceRef);
      
      console.log("Experience Snap:", experienceSnap);
      // Process each experience entry
      experienceSnap.docs.forEach(doc => {
        const exp = doc.data();
        
        // Handle date comparison
        let expDate;
        if (exp.date?.toDate) {
          // If it's a Firestore Timestamp
          expDate = exp.date.toDate();
        } else if (exp.date) {
          // If it's a string date
          expDate = new Date(exp.date);
        } else {
          // If no date, skip this entry
          return;
        }

        // Skip if entry is before last update
        if (lastUpdate && expDate <= lastUpdate) return;

        // Add to totals
        totals.totalPICHours += parseFloat((parseFloat(exp.pic || 0)).toFixed(2));
        totals.totalSICHours += parseFloat((parseFloat(exp.sic || 0)).toFixed(2));
        totals.totalSimulatorHours += parseFloat((parseFloat(exp.simulatedflight || 0)).toFixed(2));
        totals.totalSimulatorIFCHours += parseFloat((parseFloat(exp.simulatedinstrument || 0)).toFixed(2));
        totals.totalSoloHours += parseFloat((parseFloat(exp.solo || 0)).toFixed(2));
        
        // Only add to totalFlightHours if not simulator time
        if (!exp.simulatedflight) {
          totals.totalFlightHours += parseFloat((parseFloat(exp.totaltime || 0)).toFixed(2));
        }
        
        totals.totalNightHours += parseFloat((parseFloat(exp.night || 0)).toFixed(2));
        totals.totalDualInstructionHoursGiven += parseFloat((parseFloat(exp.dualgiven || 0)).toFixed(2));
        totals.totalDualInstructionHoursReceived += parseFloat((parseFloat(exp.dualreceived || 0)).toFixed(2));
        totals.totalGroundTrainingHours += parseFloat((parseFloat(exp.groundtraining || 0)).toFixed(2));
        totals.totalLandings += parseFloat((parseFloat(exp.alllandings || 0)).toFixed(2));
        totals.totalInstrument += parseFloat((parseFloat(exp.actualinstrument || 0)).toFixed(2));
        totals.totalTakeoffs += parseFloat((parseFloat(exp.daytakeoffs || 0) + parseFloat(exp.nighttakeoffs || 0)).toFixed(2));
        totals.totalNightTakeoffs += parseFloat((parseFloat(exp.nighttakeoffs || 0)).toFixed(2));
        totals.totalLandingsFullStop += parseFloat((parseFloat(exp.nightlandingsfullstop || 0)).toFixed(2));
        totals.totalLandingsNightFullStop += parseFloat((parseFloat(exp.nightlandingsfullstop || 0)).toFixed(2));
        totals.totalXCHours += parseFloat((parseFloat(exp.crosscountry || 0)).toFixed(2));
        totals.totalDistance += parseFloat((parseFloat(exp.distance || 0)).toFixed(2));
        
        // Add to IFC hours if IPC = 1
        if (exp.ipc === 1) {
          totals.totalIFCHours += parseFloat((parseFloat(exp.totaltime || 0)).toFixed(2));
        }
      });

      // Get existing totals from pilot document
      const existingTotals = {};
      Object.keys(totals).forEach(key => {
        existingTotals[key] = parseFloat((parseFloat(pilotData[key] || 0)).toFixed(2));
      });

      // Add new totals to existing totals
      const updatedTotals = {};
      Object.keys(totals).forEach(key => {
        updatedTotals[key] = existingTotals[key] + totals[key];
      });

      // Update pilot document with new totals and timestamp
      await updateDoc(pilotRef, {
        ...updatedTotals,
        experienceLastUpdated: serverTimestamp()
      });

      setSuccessMessage('Experience totals updated successfully');
    } catch (err) {
      console.error('Error scanning experience:', err);
      setError('Failed to scan experience records');
    } finally {
      setLoading(false);
    }
  };

  const handleEditType = async (designator, newTotal) => {
    try {
      const updatedHours = hoursInType.map(entry => 
        entry.designator === designator 
          ? { ...entry, total: parseFloat(newTotal) }
          : entry
      );

      const pilotRef = doc(db, `pilot/${user.uid}`);
      await updateDoc(pilotRef, {
        hours_in_type: updatedHours
      });

      setHoursInType(updatedHours);
      setEditingTypeId(null);
      setEditValue('');
    } catch (err) {
      console.error('Error updating hours:', err);
      setError('Failed to update hours');
    }
  };

  const handleEditExperience = async (experienceId, newHours) => {
    try {
      const experienceRef = doc(db, `pilot/${user.uid}/experience/${experienceId}`);
      await updateDoc(experienceRef, {
        totaltime: newHours,
        updatedAt: serverTimestamp()
      });

      // Update local state
      setExperiences(prev => prev.map(exp => 
        exp.id === experienceId 
          ? { ...exp, totaltime: newHours }
          : exp
      ));
      
      // Recalculate hours in type for this aircraft
      const experience = experiences.find(exp => exp.id === experienceId);
      if (experience?.aircraftid) {
        await updateHoursInTypeFromExperience(db, user.uid, experience.aircraftid);
      }

      setEditingExperienceId(null);
      setEditValue('');
    } catch (err) {
      console.error('Error updating experience:', err);
      setError('Failed to update experience');
    }
  };

  const updateHoursInTypeFromExperience = async (db, userId, aircraftId) => {
    try {
      // 1. Get the aircraft's typecode
      const userAircraftRef = collection(db, `pilot/${userId}/aircraft`);
      const aircraftSnapshot = await getDocs(userAircraftRef);
      const aircraft = aircraftSnapshot.docs
        .find(doc => doc.data().aircraftid === aircraftId);
      
      if (!aircraft) return;
      
      const designator = aircraft.data().typecode;
      
      // 2. Get ALL aircraft with this typecode (including duplicates - we want their hours)
      const matchingAircraft = aircraftSnapshot.docs
        .filter(doc => doc.data().typecode === designator)
        .map(doc => doc.data().aircraftid);

      // 3. Get ALL experience entries for matching aircraft (including duplicates)
      const experienceRef = collection(db, `pilot/${userId}/experience`);
      const experienceSnapshot = await getDocs(experienceRef);
      const matchingExperience = experienceSnapshot.docs
        .filter(doc => matchingAircraft.includes(doc.data().aircraftid))
        .map(doc => doc.data().totaltime || "0");

      // 4. Sum ALL hours together
      const totalHours = matchingExperience.reduce((sum, time) => {
        return sum + parseFloat(time || "0");
      }, 0);

      // 5. Update pilot document with the total
      const pilotRef = doc(db, 'pilot', userId);
      const pilotDoc = await getDoc(pilotRef);
      const currentHoursInType = pilotDoc.data()?.hours_in_type || [];

      // Remove existing entry for this designator
      const filteredHours = currentHoursInType.filter(entry => entry.designator !== designator);

      // Add updated entry with total of ALL hours
      const updatedHoursInType = [
        ...filteredHours,
        {
          designator,
          total: totalHours
        }
      ];

      await updateDoc(pilotRef, {
        hours_in_type: updatedHoursInType
      });

      return totalHours;
    } catch (err) {
      console.error('Error updating hours in type from experience:', err);
      throw err;
    }
  };

  const handleHoursInTypeScanExperience = async () => {
    try {
      setLoading(true);
      
      // Get all aircraft to map designators
      const aircraftRef = collection(db, `pilot/${user.uid}/aircraft`);
      const aircraftSnap = await getDocs(aircraftRef);
      const aircraftMap = {};
      aircraftSnap.docs.forEach(doc => {
        const data = doc.data();
        aircraftMap[data.aircraftid] = data.typecode;
      });

      // Get all experience entries
      const experienceRef = collection(db, `pilot/${user.uid}/experience`);
      const experienceSnap = await getDocs(experienceRef);
      
      // Calculate hours by type
      const hoursMap = {};
      experienceSnap.docs.forEach(doc => {
        const exp = doc.data();
        const designator = aircraftMap[exp.aircraftid];
        if (designator) {
          hoursMap[designator] = (hoursMap[designator] || 0) + parseFloat(exp.totaltime || 0);
        }
      });

      // Update pilot document
      const updatedHoursInType = Object.entries(hoursMap).map(([designator, total]) => ({
        designator,
        total
      }));

      const pilotRef = doc(db, `pilot/${user.uid}`);
      await updateDoc(pilotRef, {
        hours_in_type: updatedHoursInType
      });

      setHoursInType(updatedHoursInType);
      setSuccessMessage('Hours in type updated from experience records');
    } catch (err) {
      console.error('Error scanning hours in type:', err);
      setError('Failed to scan hours in type');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickEntryUpdate = async () => {
    try {
      setLoading(true);
      setError(null);

      const pilotRef = doc(db, `pilot/${user.uid}`);
      const experienceRef = collection(db, `pilot/${user.uid}/experience`);
      
      // Create new experience entry
      const newExperience = {
        ...quickEntryData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        date: new Date().toISOString()
      };

      // Add to experience collection
      await addDoc(experienceRef, newExperience);

      // Update pilot totals
      await updateDoc(pilotRef, {
        flightHoursLast30Days: parseFloat((parseFloat(quickEntryData.flightHoursLast30Days || 0)).toFixed(2)),
        last90DaysFlightHours: parseFloat((parseFloat(quickEntryData.last90DaysFlightHours || 0)).toFixed(2)),
        nightHoursLast30Days: parseFloat((parseFloat(quickEntryData.nightHoursLast30Days || 0)).toFixed(2)),
        totalPICHours: increment(parseFloat((parseFloat(quickEntryData.pic || 0)).toFixed(2))),
        totalSICHours: increment(parseFloat((parseFloat(quickEntryData.sic || 0)).toFixed(2))),
        totalSimulatorHours: increment(parseFloat((parseFloat(quickEntryData.simulatedflight || 0)).toFixed(2))),
        totalSimulatorIFCHours: increment(parseFloat((parseFloat(quickEntryData.simulatedinstrument || 0)).toFixed(2))),
        totalSoloHours: increment(parseFloat((parseFloat(quickEntryData.solo || 0)).toFixed(2))),
        totalFlightHours: increment(parseFloat((parseFloat(quickEntryData.totaltime || 0)).toFixed(2))),
        totalNightHours: increment(parseFloat((parseFloat(quickEntryData.night || 0)).toFixed(2))),
        totalDualInstructionHoursGiven: increment(parseFloat((parseFloat(quickEntryData.dualgiven || 0)).toFixed(2))),
        totalDualInstructionHoursReceived: increment(parseFloat((parseFloat(quickEntryData.dualreceived || 0)).toFixed(2))),
        totalGroundTrainingHours: increment(parseFloat((parseFloat(quickEntryData.groundtraining || 0)).toFixed(2))),
        totalLandings: increment(parseInt(quickEntryData.alllandings || 0)),
        totalInstrument: increment(parseFloat((parseFloat(quickEntryData.actualinstrument || 0)).toFixed(2))),
        totalTakeoffs: increment(parseInt(quickEntryData.daytakeoffs || 0) + parseInt(quickEntryData.nighttakeoffs || 0)),
        totalNightTakeoffs: increment(parseInt(quickEntryData.nighttakeoffs || 0)),
        totalLandingsFullStop: increment(parseInt(quickEntryData.nightlandingsfullstop || 0)),
        totalLandingsNightFullStop: increment(parseInt(quickEntryData.nightlandingsfullstop || 0)),
        totalXCHours: increment(parseFloat((parseFloat(quickEntryData.crosscountry || 0)).toFixed(2))),
        totalDistance: increment(parseFloat((parseFloat(quickEntryData.distance || 0)).toFixed(2))),
        experienceLastUpdated: serverTimestamp()
      });

      setSuccessMessage('Experience updated successfully');
    } catch (err) {
      console.error('Error updating experience:', err);
      setError('Failed to update experience');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <div className="space-y-4">
      

      {/* Quick Entry Section Label */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Quick Entry</h3>
        <button
          onClick={handleScanExperience}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
          disabled={loading}
        >
          {loading ? 'Scanning...' : 'Scan Experience'}
        </button>
      </div>
      
      {/* Quick Entry Rows */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        <div className="col-span-2 md:col-span-3 lg:col-span-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Last 30 Days</label>
          <input
            type="number"
            step="0.1"
            value={quickEntryData.flightHoursLast30Days}
            onChange={(e) => setQuickEntryData(prev => ({ 
              ...prev, 
              flightHoursLast30Days: e.target.value 
            }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Last 90 Days</label>
          <input
            type="number"
            step="0.1"
            value={quickEntryData.last90DaysFlightHours}
            onChange={(e) => setQuickEntryData(prev => ({ 
              ...prev, 
              last90DaysFlightHours: e.target.value 
            }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Night Last 30</label>
          <input
            type="number"
            step="0.1"
            value={quickEntryData.nightHoursLast30Days}
            onChange={(e) => setQuickEntryData(prev => ({ 
              ...prev, 
              nightHoursLast30Days: e.target.value 
            }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">PIC Hours</label>
          <input
            type="number"
            step="0.1"
            value={quickEntryData.pic}
            onChange={(e) => setQuickEntryData(prev => ({ ...prev, pic: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">SIC Hours</label>
          <input
            type="number"
            step="0.1"
            value={quickEntryData.sic}
            onChange={(e) => setQuickEntryData(prev => ({ ...prev, sic: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Simulator Hours</label>
          <input
            type="number"
            step="0.1"
            value={quickEntryData.simulatedflight}
            onChange={(e) => setQuickEntryData(prev => ({ ...prev, simulatedflight: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Simulated Instrument</label>
          <input
            type="number"
            step="0.1"
            value={quickEntryData.simulatedinstrument}
            onChange={(e) => setQuickEntryData(prev => ({ ...prev, simulatedinstrument: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Solo Hours</label>
          <input
            type="number"
            step="0.1"
            value={quickEntryData.solo}
            onChange={(e) => setQuickEntryData(prev => ({ ...prev, solo: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Total Time</label>
          <input
            type="number"
            step="0.1"
            value={quickEntryData.totaltime}
            onChange={(e) => setQuickEntryData(prev => ({ ...prev, totaltime: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Night Hours</label>
          <input
            type="number"
            step="0.1"
            value={quickEntryData.night}
            onChange={(e) => setQuickEntryData(prev => ({ ...prev, night: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Dual Given</label>
          <input
            type="number"
            step="0.1"
            value={quickEntryData.dualgiven}
            onChange={(e) => setQuickEntryData(prev => ({ ...prev, dualgiven: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Dual Received</label>
          <input
            type="number"
            step="0.1"
            value={quickEntryData.dualreceived}
            onChange={(e) => setQuickEntryData(prev => ({ ...prev, dualreceived: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Ground Training</label>
          <input
            type="number"
            step="0.1"
            value={quickEntryData.groundtraining}
            onChange={(e) => setQuickEntryData(prev => ({ ...prev, groundtraining: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">All Landings</label>
          <input
            type="number"
            value={quickEntryData.alllandings}
            onChange={(e) => setQuickEntryData(prev => ({ ...prev, alllandings: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Actual Instrument</label>
          <input
            type="number"
            step="0.1"
            value={quickEntryData.actualinstrument}
            onChange={(e) => setQuickEntryData(prev => ({ ...prev, actualinstrument: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Day Takeoffs</label>
          <input
            type="number"
            value={quickEntryData.daytakeoffs}
            onChange={(e) => setQuickEntryData(prev => ({ ...prev, daytakeoffs: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Night Takeoffs</label>
          <input
            type="number"
            value={quickEntryData.nighttakeoffs}
            onChange={(e) => setQuickEntryData(prev => ({ ...prev, nighttakeoffs: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Night Landings Full Stop</label>
          <input
            type="number"
            value={quickEntryData.nightlandingsfullstop}
            onChange={(e) => setQuickEntryData(prev => ({ ...prev, nightlandingsfullstop: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Cross Country</label>
          <input
            type="number"
            step="0.1"
            value={quickEntryData.crosscountry}
            onChange={(e) => setQuickEntryData(prev => ({ ...prev, crosscountry: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Distance (nm)</label>
          <input
            type="number"
            value={quickEntryData.distance}
            onChange={(e) => setQuickEntryData(prev => ({ ...prev, distance: e.target.value }))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        <div>
          <button
            onClick={handleQuickEntryUpdate}
            disabled={loading}
            className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Updating...</span>
              </>
            ) : (
              <>
                <span>Update Experience</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>

      {/* Hours in Type Section */}
      <div className="text-left mt-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Hours in Type</h3>
          <button
            onClick={handleHoursInTypeScanExperience}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
            disabled={loading}
          >
            {loading ? 'Scanning...' : 'Scan Type Experience'}
          </button>
        </div>
        
        <div className="flex space-x-4 items-center w-full mb-4">
          <select
            value={selectedTypeDesignator}
            onChange={(e) => setSelectedTypeDesignator(e.target.value)}
            className="border rounded-lg px-2 py-1 w-1/3"
          >
            <option value="">Select Type</option>
            {availableDesignators.map((designator) => (
              <option key={designator} value={designator}>
                {designator}
              </option>
            ))}
          </select>
          <input
            type="number"
            value={typeHours}
            onChange={(e) => setTypeHours(e.target.value)}
            placeholder="Total Hours"
            className="border rounded-lg px-2 py-1 w-1/3"
          />
          <button
            onClick={handleUpdateHoursInType}
            className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
          >
            Update
          </button>
        </div>

        {/* Display current hours in type */}
        <div className="mt-4">
          <table className="min-w-min bg-white border border-gray-200">
            <thead>
              <tr className="bg-gray-100">
                <th className="py-2 px-4 border-b text-neutral-white">Aircraft Type</th>
                <th className="py-2 px-4 border-b text-neutral-white">Total Hours</th>
                <th className="py-2 px-4 border-b text-neutral-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {hoursInType
                .sort((a, b) => a.designator.localeCompare(b.designator))
                .map((entry) => (
                <tr key={entry.designator} className="text-center hover:bg-neutral-50">
                  <td className="py-2 px-4 border-b">{entry.designator}</td>
                  <td className="py-2 px-4 border-b">
                    {editingTypeId === entry.designator ? (
                      <input
                        type="number"
                        step="0.01"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="w-24 border rounded px-2 py-1"
                        autoFocus
                      />
                    ) : (
                      Number(entry.total).toFixed(1)
                    )}
                  </td>
                  <td className="py-2 px-4 border-b">
                    {editingTypeId === entry.designator ? (
                      <div className="flex justify-center space-x-2">
                        <button
                          onClick={() => handleEditType(entry.designator, editValue)}
                          className="text-green-500 hover:text-green-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingTypeId(null);
                            setEditValue('');
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingTypeId(entry.designator);
                          setEditValue(entry.total.toString());
                        }}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bulk Entry Section Label */}
      <div className="text-left mt-4">
        <h3 className="text-lg font-semibold">Bulk Entry</h3>
      </div>

      {/* Top Buttons */}
      <div className="flex justify-between items-center space-x-4">
        <CSVImportHandler 
          db={db}
          userId={user.uid}
          type="experience" // Pass type as "experience"
          onSuccess={() => onComplete && onComplete()}
          onDataRefresh={() => {}} // Implement refresh logic if needed
        />
        <button
          onClick={handleAddExperience}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
        >
          Add Experience
        </button>
      </div>

      {/* Experience Table */}
      <div className="mt-4">
        <h3 className="text-lg font-semibold">Flight Experience</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 w-full">
            <thead>
              <tr className="bg-gray-100">
                <th className="py-2 px-4 border-b text-neutral-white">Date</th>
                <th className="py-2 px-4 border-b text-neutral-white">Type</th>
                <th className="py-2 px-4 border-b text-neutral-white">Aircraft</th>
                <th className="py-2 px-4 border-b text-neutral-white">Hours</th>
                <th className="py-2 px-4 border-b text-neutral-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {experiences.map(exp => (
                <tr key={exp.id} className="text-center hover:bg-neutral-50">
                  <td className="py-2 px-4 border-b">{new Date(exp.date).toDateString()}</td>
                  <td className="py-2 px-4 border-b">{exp.type}</td>
                  <td className="py-2 px-4 border-b">{exp.aircraftid}</td>
                  <td className="py-2 px-4 border-b">
                    {editingExperienceId === exp.id ? (
                      <input
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="w-24 border rounded px-2 py-1"
                        autoFocus
                      />
                    ) : (
                      exp.totaltime
                    )}
                  </td>
                  <td className="py-2 px-4 border-b">
                    {editingExperienceId === exp.id ? (
                      <div className="flex justify-center space-x-2">
                        <button
                          onClick={() => handleEditExperience(exp.id, editValue)}
                          className="text-green-500 hover:text-green-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingExperienceId(null);
                            setEditValue('');
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingExperienceId(exp.id);
                          setEditValue(exp.totaltime);
                        }}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ExperienceSection;
